/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useContext, useRef, useCallback } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Badge from "@mui/material/Badge";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import { useMaterialUIController, setTransparentNavbar, setMiniSidenav } from "context";

// Auth context
import AuthContext from "context/AuthContext";

// API
import api from "api/axios";

// Noms de page affichés (accentués), pour ne pas dériver le titre du slug
// d'URL (ex. "procedures-internes" -> "Procedures Internes", sans accent).
// Importé depuis routeNames.js (et non routes.js) pour éviter un cycle
// d'import : routes.js -> layouts/* -> DashboardNavbar -> routes.js.
import routeNames from "routeNames";

function findRouteName(pathname) {
  const actual = pathname.split("/").filter(Boolean);
  const match = routeNames.find((r) => {
    if (!r.route) return false;
    const pattern = r.route.split("/").filter(Boolean);
    if (pattern.length !== actual.length) return false;
    return pattern.every((seg, i) => seg.startsWith(":") || seg === actual[i]);
  });
  return match?.name;
}

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const location = useLocation();
  const route = location.pathname.split("/").slice(1);
  const pageTitle = findRouteName(location.pathname) || route[route.length - 1];
  const navigate = useNavigate();

  // Auth
  const { user, logout } = useContext(AuthContext);

  // Menus
  const [profileMenu, setProfileMenu] = useState(null);
  const [notifMenu, setNotifMenu] = useState(null);

  // Recherche globale
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTimer = useRef(null);

  const TYPE_LABELS = {
    procedure: { label: "Procédure", color: "#1565c0" },
    texte: { label: "Texte", color: "#186A81" },
    exigence: { label: "Exigence", color: "#6a1b9a" },
    risque: { label: "Risque", color: "#c62828" },
  };

  const handleSearch = useCallback(async (val) => {
    if (val.length < 2) {
      setSearchResults(null);
      return;
    }
    setSearchLoading(true);
    try {
      const { data } = await api.get(`/api/search?q=${encodeURIComponent(val)}`);
      setSearchResults(data.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setSearchOpen(true);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => handleSearch(val), 350);
  };

  // Notifications
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  // Fetch notifications (seulement les non lues)
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await api.get("/api/notifications/");
        const all = Array.isArray(res.data) ? res.data : [];
        const unread = all
          .filter((n) => !n.lu)
          .sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation))
          .slice(0, 10);
        setNotifications(unread);
      } catch (err) {
        console.error("Erreur chargement notifications:", err);
      }
    };
    fetchNotifications();
  }, []);

  const markNavNotifAsRead = async (notif) => {
    setNotifMenu(null);
    try {
      await api.patch(`/api/notifications/${notif.id}/lu`);
      setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
    } catch (err) {
      console.error("Erreur marquage notification:", err);
    }
  };

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  const handleLogout = async () => {
    setProfileMenu(null);
    await logout();
    navigate("/authentication/sign-in");
  };

  // Nom utilisateur
  const userName = user?.profile?.name || user?.nom || user?.email || "Utilisateur";
  const userRole = user?.profile?.role || user?.role || "";

  const unreadCount = notifications.filter((n) => !n.lu).length;

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={pageTitle} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            {/* Nom de l'utilisateur */}
            <MDBox display="flex" alignItems="center" mr={1}>
              <MDTypography variant="button" fontWeight="medium" color={light ? "white" : "dark"}>
                {userName}
              </MDTypography>
              {userRole && (
                <MDTypography variant="caption" color="text" ml={0.5}>
                  ({userRole})
                </MDTypography>
              )}
            </MDBox>

            {/* ── Recherche globale ── */}
            <MDBox sx={{ position: "relative", mr: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.4,
                  minWidth: 220,
                  bgcolor: "background.paper",
                }}
              >
                <Icon sx={{ fontSize: "1rem", color: "text.secondary", mr: 0.5 }}>search</Icon>
                <InputBase
                  placeholder="Rechercher…"
                  value={searchQuery}
                  onChange={onSearchChange}
                  onFocus={() => setSearchOpen(true)}
                  onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                  sx={{ fontSize: "0.8rem", width: "100%" }}
                />
                {searchLoading && <CircularProgress size={14} />}
              </Paper>
              {searchOpen && searchResults && (
                <Paper
                  elevation={4}
                  sx={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: 0,
                    right: 0,
                    zIndex: 9999,
                    maxHeight: 360,
                    overflowY: "auto",
                    borderRadius: 2,
                  }}
                >
                  {searchResults.length === 0 ? (
                    <MenuItem disabled sx={{ fontSize: "0.8rem", opacity: 0.7 }}>
                      Aucun résultat
                    </MenuItem>
                  ) : (
                    searchResults.map((r, i) => {
                      const meta = TYPE_LABELS[r.type] || { label: r.type, color: "#757575" };
                      return (
                        <MenuItem
                          key={i}
                          onMouseDown={() => {
                            setSearchQuery("");
                            setSearchResults(null);
                            navigate(r.route);
                          }}
                          sx={{ gap: 1, alignItems: "flex-start", py: 1 }}
                        >
                          <Chip
                            label={meta.label}
                            size="small"
                            sx={{
                              bgcolor: meta.color + "22",
                              color: meta.color,
                              fontWeight: 700,
                              fontSize: "0.6rem",
                              height: 18,
                              mt: 0.2,
                              flexShrink: 0,
                            }}
                          />
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              fontWeight="medium"
                              sx={{ display: "block", lineHeight: 1.3 }}
                            >
                              {r.titre}
                            </MDTypography>
                            {r.description && (
                              <MDTypography
                                variant="caption"
                                color="text"
                                sx={{ opacity: 0.6, fontSize: "0.65rem" }}
                              >
                                {r.description}
                              </MDTypography>
                            )}
                          </MDBox>
                        </MenuItem>
                      );
                    })
                  )}
                </Paper>
              )}
            </MDBox>

            <MDBox color={light ? "white" : "inherit"} display="flex" alignItems="center">
              {/* Notifications */}
              <Tooltip title="Notifications">
                <IconButton
                  size="small"
                  disableRipple
                  sx={navbarIconButton}
                  onClick={(e) => setNotifMenu(e.currentTarget)}
                >
                  <Badge badgeContent={unreadCount} color="error" max={99}>
                    <Icon sx={iconsStyle}>notifications</Icon>
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* Profil */}
              <Tooltip title="Mon profil">
                <IconButton
                  size="small"
                  disableRipple
                  sx={navbarIconButton}
                  onClick={(e) => setProfileMenu(e.currentTarget)}
                >
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
              </Tooltip>

              {/* Menu mobile sidenav */}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
            </MDBox>

            {/* Menu déroulant Profil */}
            <Menu
              anchorEl={profileMenu}
              open={Boolean(profileMenu)}
              onClose={() => setProfileMenu(null)}
              sx={{ mt: 2 }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  setProfileMenu(null);
                  navigate("/profil");
                }}
              >
                <Icon sx={{ mr: 1 }}>person</Icon>
                Mon Profil
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
                <Icon sx={{ mr: 1 }}>logout</Icon>
                Déconnexion
              </MenuItem>
            </Menu>

            {/* Menu déroulant Notifications */}
            <Menu
              anchorEl={notifMenu}
              open={Boolean(notifMenu)}
              onClose={() => setNotifMenu(null)}
              sx={{ mt: 2, maxHeight: 400 }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {notifications.length === 0 ? (
                <MenuItem disabled sx={{ opacity: 0.6 }}>
                  <Icon sx={{ mr: 1, fontSize: 18, color: "grey.400" }}>notifications_none</Icon>
                  <MDTypography variant="caption">Aucune nouvelle notification</MDTypography>
                </MenuItem>
              ) : (
                notifications.map((notif) => (
                  <MenuItem
                    key={notif.id}
                    onClick={() => markNavNotifAsRead(notif)}
                    sx={{
                      maxWidth: 320,
                      borderLeft: "3px solid #1A73E8",
                      mb: 0.5,
                      borderRadius: 1,
                      "&:hover": { backgroundColor: "rgba(26,115,232,0.06)" },
                    }}
                  >
                    <MDBox display="flex" alignItems="flex-start" gap={1}>
                      <Icon sx={{ fontSize: 18, color: "info.main", mt: 0.2, flexShrink: 0 }}>
                        {notif.texte_id ? "description" : "notifications"}
                      </Icon>
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          color="dark"
                          sx={{ display: "block", whiteSpace: "normal", lineHeight: 1.4 }}
                        >
                          {notif.message}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          sx={{ opacity: 0.6, fontSize: "0.65rem" }}
                        >
                          Cliquer pour marquer comme lu
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MenuItem>
                ))
              )}
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  setNotifMenu(null);
                  navigate("/notifications");
                }}
                sx={{ justifyContent: "center" }}
              >
                <MDTypography variant="caption" color="info" fontWeight="medium">
                  Voir toutes les notifications
                </MDTypography>
              </MenuItem>
            </Menu>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
